import { useState, useEffect, useCallback } from 'react';
import { Activity, RefreshCw, Zap, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

import { fmtFixed } from '../../utils/format';
const BalanceSyncHealth = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchHealth = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const res = await api.get('/admin/balance-sync/health?limit_examples=50', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (error) {
      console.error('Balance sync health error:', error);
      toast.error('Failed to load balance sync health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const handleBackfill = async () => {
    if (!window.confirm('Run wallet heal for every user with a legacy balance drift? Idempotent, safe.')) return;
    try {
      setRunning(true);
      const token = localStorage.getItem('adminToken');
      const res = await api.post('/admin/balance-sync/backfill', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data?.message || 'Backfill complete');
      await fetchHealth();
    } catch (error) {
      console.error('Backfill error:', error);
      toast.error('Backfill failed');
    } finally {
      setRunning(false);
    }
  };

  const pct = data?.sync_percentage ?? 100;
  const healthy = pct >= 99;

  return (
    <div className="space-y-6" data-testid="balance-sync-health-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="h-6 w-6 text-emerald-400" /> Balance Sync Health
          </h1>
          <p className="text-gray-400 text-sm">Detect users whose admin balance drifts from their wallet total.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchHealth}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-lg text-white font-semibold text-sm"
            data-testid="balance-sync-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            onClick={handleBackfill}
            disabled={running || !data?.drifting_count}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-black font-bold text-sm"
            data-testid="balance-sync-backfill-btn"
          >
            <Zap className="h-4 w-4" /> {running ? 'Healing…' : 'Heal Now'}
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className={`rounded-xl p-5 border ${healthy ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-amber-500/10 border-amber-500/40'}`}>
          <div className="flex items-center gap-3 mb-2">
            {healthy ? <CheckCircle2 className="h-6 w-6 text-emerald-300" /> : <AlertTriangle className="h-6 w-6 text-amber-300" />}
            <p className={`text-xs ${healthy ? 'text-emerald-200' : 'text-amber-200'}`}>Sync Rate</p>
          </div>
          <p className="text-white text-3xl font-bold">{pct.toFixed(1)}%</p>
          <p className={`text-xs mt-1 ${healthy ? 'text-emerald-100/70' : 'text-amber-100/70'}`}>
            {data?.in_sync ?? 0} of {data?.total_users ?? 0} users aligned
          </p>
        </div>
        <div className="rounded-xl p-5 border bg-gray-800 border-gray-700">
          <p className="text-gray-400 text-xs mb-2">Drifting users</p>
          <p className="text-white text-3xl font-bold">{data?.drifting_count ?? 0}</p>
          <p className="text-gray-400 text-xs mt-1">|delta| ≥ $1 between admin & wallet</p>
        </div>
        <div className="rounded-xl p-5 border bg-gray-800 border-gray-700">
          <p className="text-gray-400 text-xs mb-2">Total users scanned</p>
          <p className="text-white text-3xl font-bold">{data?.total_users ?? 0}</p>
          <p className="text-gray-400 text-xs mt-1">Last check: {data?.checked_at ? new Date(data.checked_at).toLocaleTimeString() : '—'}</p>
        </div>
      </div>

      {/* Drift table */}
      <div className="bg-gray-900 rounded-xl border border-gray-800">
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-white font-bold">Top drifting users</h3>
          <span className="text-xs text-gray-400">Sorted by biggest delta</span>
        </div>
        {loading ? (
          <div className="p-8 text-gray-400 text-sm">Loading…</div>
        ) : !data?.drifting_users?.length ? (
          <div className="p-6 flex items-center gap-2 text-emerald-300 text-sm">
            <CheckCircle2 className="h-4 w-4" /> All users are in sync. Nothing to fix.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="balance-sync-drift-table">
              <thead className="bg-gray-800 text-gray-400">
                <tr>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-right px-4 py-2">Admin balance</th>
                  <th className="text-right px-4 py-2">Wallet balance</th>
                  <th className="text-right px-4 py-2">Delta (USDT)</th>
                  <th className="text-left px-4 py-2">Healed before?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {data.drifting_users.map((u) => (
                  <tr key={u.user_id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-2 text-white">{u.email || u.user_id}</td>
                    <td className="px-4 py-2 text-right text-gray-200 font-mono">${fmtFixed(u.admin_balance, 2)}</td>
                    <td className="px-4 py-2 text-right text-gray-200 font-mono">${fmtFixed(u.wallet_balance, 2)}</td>
                    <td className={`px-4 py-2 text-right font-mono font-bold ${u.delta > 0 ? 'text-amber-300' : 'text-rose-300'}`}>
                      {u.delta > 0 ? '+' : ''}${fmtFixed(u.delta, 2)}
                    </td>
                    <td className="px-4 py-2 text-gray-400 text-xs">{u.healed_before ? 'yes' : 'no'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BalanceSyncHealth;
